import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks';
import { getStudentOfflineAssignments, getStudentOnlineAssignments } from '../api';
import {
  mergeAssignmentLists,
  toAssignmentList
} from '../components/Activity/utils/toAssignmentList';

const statusOf = (error) => error?.response?.status;
const messageOf = (error) =>
  String(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      ''
  );

/** Offline endpoint rejects non-offline student accounts with 400. */
const isOfflineAccountMismatch = (error) =>
  statusOf(error) === 400 && /offline/i.test(messageOf(error));

/**
 * Prod/staging do not have getStudentOnlineAssignments yet.
 * The request then hits GET /student-assignments/:id and fails ObjectId validation.
 */
const isMissingOnlineRoute = (error) => {
  const status = statusOf(error);
  if (status === 404) return true;
  if (status === 400) return true;
  return /invalid student assignment/i.test(messageOf(error));
};

export const useGetStudentOfflineAssignments = () => {
  const { token } = useAuth();
  return useQuery({
    queryKey: ['student-offline-assignments'],
    queryFn: async () => {
      try {
        return await getStudentOfflineAssignments({ token });
      } catch (error) {
        if (isOfflineAccountMismatch(error)) return [];
        throw error;
      }
    },
    enabled: !!token,
    select: toAssignmentList
  });
};

export const useGetStudentOnlineAssignments = () => {
  const { token } = useAuth();
  return useQuery({
    queryKey: ['student-online-assignments'],
    queryFn: async () => {
      try {
        return await getStudentOnlineAssignments({ token });
      } catch (error) {
        if (isMissingOnlineRoute(error)) return [];
        throw error;
      }
    },
    enabled: !!token,
    select: toAssignmentList
  });
};

export const useStudentActivityAssignments = () => {
  const offlineQuery = useGetStudentOfflineAssignments();
  const onlineQuery = useGetStudentOnlineAssignments();

  const data = useMemo(() => {
    const online = toAssignmentList(onlineQuery.data).map((item) => ({
      ...item,
      source: item.source || 'online'
    }));
    const offline = toAssignmentList(offlineQuery.data).map((item) => ({
      ...item,
      source: 'offline'
    }));
    return mergeAssignmentLists(online, offline);
  }, [offlineQuery.data, onlineQuery.data]);

  const hasData = data.length > 0;
  const bothFailed = offlineQuery.isError && onlineQuery.isError;

  return {
    data,
    isLoading: (offlineQuery.isLoading || onlineQuery.isLoading) && !hasData,
    isFetching: offlineQuery.isFetching || onlineQuery.isFetching,
    isError: bothFailed && !hasData,
    error: onlineQuery.error || offlineQuery.error,
    refetch: () => Promise.all([offlineQuery.refetch(), onlineQuery.refetch()])
  };
};
